// Static copy + data for the public Career OS Bundle offer / paid-ads landing
// page. Kept separate from page.tsx so the route file stays under the repo's
// 300-line limit and the copy is easy to edit without touching markup.
//
// Channel: Google Search (intent capture, NOT demand generation). The click
// comes from queries like "review cv gratis", "cek cv ats", "cv ats fresh
// graduate". The page must message-match that search intent: lead with the
// free CV review / ATS check, then upsell the Rp79k Career OS Bundle.
// No resign/career-switch hook here (that is the Meta demand-gen angle).
// Proof is real only: no resetting countdowns, no invented stats.

export const PAGE_SLUG = "career-booster";
export const BASE_URL = "https://hyred.id";

export const BUNDLE_PRICE = 79000;
export const BUNDLE_ANCHOR = 350000;

// Meta targets the commercial-intent cluster (jasa review cv, cv rewrite,
// career os) so this lander stops cannibalizing /review-cv-gratis in search.
// The free-review hook stays in the HERO copy below for ads message-match.
// The layout title template appends "| hyred", so no brand suffix here.
export const META = {
  title: "Career OS Bundle Rp79.000: Review dan Rewrite CV",
  description:
    "Career OS Bundle Rp79.000 sekali bayar: 5x review dan rewrite CV per posisi incaran, 2x mock interview AI, personalisasi CV, optimasi LinkedIn, dan analisis job fit. Hemat 77% dari Rp350.000. Mulai dari review CV gratis dulu.",
};

// StoryBrand one-liner: [character] + [problem] + [solution] + [result].
// Reused as the hero subhead and shareable elevator pitch.
export const ONE_LINER =
  "Hyred bantu pencari kerja tahu kenapa lamarannya ditolak ATS dan bantu benerin CV-nya, biar dapat panggilan interview.";

// Hero (desire-led). ATS jargon is demoted to SEO sections (META + FAQ); the
// emotional copy speaks the felt outcome: lolos screening, dapat panggilan.
export const HERO = {
  eyebrow: "Review CV Gratis",
  headline: "Lolos screening CV dan dapat lebih banyak panggilan interview",
  subhead: ONE_LINER,
};

// El.2 villain framing. ONE villain (silent rejection) with ATS as its named
// weapon, plus the philosophical injustice that absolves the candidate.
export const VILLAIN = {
  heading: "Banyak lamaran yang kamu buat ditolak diam-diam.",
  body: "Kamu apply, lalu nggak ada kabar. CVmu kefilter ATS tanpa pernah dibaca sama recruiter, kadang format dan isinya bikin diskip recruiter, kadang nggak pernah tahu ditolaknya kenapa. Kamu nggak harus jadi ahli rekrutmen biar dapat panggilan kerja.",
};

// El.3 guide empathy. Mirrors the internal arc (confusion -> exhaustion ->
// self-doubt) and shifts blame onto the villain, never the candidate. Rendered
// directly above the proof bar so empathy + authority read as one trust unit.
export const EMPATHY =
  "Kami mengerti, apply berkali-kali tanpa kejelasan itu bikin capek dan ragu sama diri sendiri. Tapi masalahnya bukan kamu atau pengalaman kamu, cuma belum ada yang nunjukin bagian mana yang harus dibenerin.";

// El.6 failure stakes. Opportunity cost tied to the villain, no invented
// scarcity. Rendered once, right before the bundle CTA.
export const STAKES =
  "Tiap minggu CVmu masih sama, posisi yang kamu incar diisi orang lain, dan kamu mulai lagi dari awal.";

// El.7 success picture. Closes the gap from the desire: dead villain (panggilan
// masuk) + identity shift (pede, lawan dari self-doubt di awal).
export const SUCCESS =
  "Bayangkan: CV kamu lolos screening, panggilan interview mulai masuk, dan kamu apply dengan percaya diri, bukan lagi ngandelin hoki nunggu kabar yang nggak pasti.";

// 4-panel StoryBrand comic (flat editorial, wordless art). Captions render as
// HTML below the strip so they stay crisp and translatable, not baked into the
// image. Order = the narrative arc: villain -> guide -> plan -> success.
export const COMIC = {
  src: "/blog/career-booster-comic.webp",
  alt: "Komik empat panel: pencari kerja yang lamarannya sepi, lalu pakai Hyred untuk benahi CV, sampai akhirnya dapat undangan interview.",
  width: 1983,
  height: 793,
  panels: [
    "Apply terus, tapi nggak dapat panggilan interview. Nggak ada yang kasih tahu apa yang salah.",
    "Hyred bantu cek CV kamu dan tunjukin persis bagian yang bikin ke-skip.",
    "CV, LinkedIn, dan cover letter dirapikan biar siap apply.",
    "Undangan interview mulai masuk. Sekarang kamu apply dengan percaya diri.",
  ],
};

// Absolute URL for OG/Twitter metadata; local path for the <Image> on the page
// (next/image needs the host allowlisted, so render the local asset instead).
export const OG_IMAGE = `${BASE_URL}/blog/career-booster-hero.webp`;
export const HERO_IMAGE = "/blog/career-booster-hero.webp";
export const HERO_IMAGE_W = 1672;
export const HERO_IMAGE_H = 941;

// Pain agitation matched to search intent: people looking up "cv ats" /
// "review cv" already know they have a CV problem. The pain is silent
// rejection and ATS filtering, not "should I resign".
export const PAINS = [
  {
    title: "CV diskip recruiter tanpa kabar",
    body: "Banyak perusahaan pakai ATS. Kalau CV kamu nggak kebaca mesin, lamaran gugur sebelum sampai ke recruiter.",
  },
  {
    title: "Apply terus tapi interview nggak masuk-masuk",
    body: "Kamu apply terus tapi nggak ada kabar, dan nggak ada yang kasih tahu bagian mana yang bikin CV kamu ditolak.",
  },
  {
    title: "Format CV nggak kebaca ATS",
    body: "Dua kolom, ikon, tabel, dan teks di dalam gambar sering bikin ATS salah baca, lalu CVmu nggak pernah dibaca manusia.",
  },
];

// Internal arc: confusion ("nggak tahu apa yang salah") -> exhaustion ("capek")
// -> self-doubt ("ragu sama diri sendiri"). Blame stays on the silent process.
export const PAIN_CLIMAX =
  "Kamu kirim CV yang sama berkali-kali tanpa tahu apa yang salah, lama-lama capek, dan mulai ragu sama diri sendiri, padahal masalahnya cuma di CV yang belum kebaca ATS.";

// 4-step plan (StoryBrand process plan). Action-verb names; step 2 is the
// confusion-killer, step 4 bridges into the success picture (El.7).
export const STEPS = [
  {
    name: "Upload CV kamu, gratis",
    text: "Upload CV (PDF), AI Hyred bantu cek 8 kriteria ATS dalam 2 menit dan kasih skor. Gratis, tanpa syarat.",
  },
  {
    name: "Tahu persis mana yang salah",
    text: "Skor di bawah 60 berarti CV kamu berisiko ditolak ATS. Kamu tahu bagian mana yang harus diperbaiki, bukan cuma lolos atau tidak.",
  },
  {
    name: "Beresin sekalian",
    text: "Career OS Bundle bantu review & tulis ulang CV (5x), siapkan mock interview AI, personalisasi CV, optimasi LinkedIn, dan analisis ideal job fit, semua dalam satu paket.",
  },
  {
    name: "Apply dengan percaya diri",
    text: "Kirim lamaran dengan percaya diri, dan mulai dapat panggilan interview setelah ini.",
  },
];

// Bundle deliverables (same Rp79k product). Standalone prices shown for
// transparent value stacking, not invented scarcity.
export const KIT_CORE = [
  { name: "5x CV Review & CV Tailored", note: "AI susun ulang CV per posisi incaran" },
  { name: "2x Mock Interview AI", note: "Latihan wawancara dengan AI, dapat skor & feedback" },
  { name: "Personalisasi CV x1", note: "" },
  { name: "Optimasi LinkedIn x1", note: "" },
  { name: "Analisis Ideal Job Fit x1", note: "" },
];

export const KIT_BONUS = [
  { name: "Akses komunitas dukungan karier", price: 127000 },
  { name: "Webinar karier bulanan", price: 126000 },
];

// Real, defensible proof. {{cvsReviewed}} = completed free CV reviews, counted
// live and rounded down (proof-stats-server.ts); 600+ = ulasan yang ditulis
// pengguna ke platform; 4,9/5 dari 631 rating (aggregate-rating.ts).
export const PROOF_POINTS = [
  { stat: "{{cvsReviewed}}", label: "CV direview AI" },
  { stat: "600+", label: "ulasan pengguna" },
  { stat: "4,9/5", label: "rating dari 631 peserta" },
];

export const FAQ_ITEMS = [
  {
    question: "Review CV-nya benar-benar gratis?",
    answer:
      "Ya. Upload pertama dan review AI pertama gratis, tanpa syarat. Career OS Bundle adalah upgrade opsional kalau kamu mau CV disusun ulang dan dapat paket dukungan karier lengkap.",
  },
  {
    question: "Bagaimana cara cek CV lolos ATS atau tidak?",
    answer:
      "Upload CV kamu, lalu AI Hyred membacanya seperti ATS dan menandai bagian yang berisiko tidak terbaca: layout dua kolom, heading tidak standar, ikon, tabel, atau teks di dalam gambar. Kamu langsung tahu apa yang harus diperbaiki.",
  },
  {
    question: "Cocok untuk fresh graduate?",
    answer:
      "Sangat cocok. Masalah paling umum di CV fresh grad bukan kurang pengalaman, tapi cara menyampaikan magang, organisasi, dan proyek kuliah supaya terbaca relevan. AI Hyred menandai bagian yang lemah dan memberi contoh perbaikan.",
  },
  {
    question: "Ini bukan joki CV, kan?",
    answer:
      "Bukan. Kamu yang cerita pengalaman kamu, Hyred yang menyusun dokumennya. Hasilnya bisa kamu edit dan rapikan sendiri, jadi kontrol tetap di kamu.",
  },
  {
    question: "Kenapa Career OS Bundle Rp79.000, padahal nilainya Rp350.000?",
    answer:
      "Kalau dibeli terpisah, modul dan bonusnya totalnya sekitar Rp350.000. Dalam satu paket Career OS Bundle harganya Rp79.000 sekali bayar, hemat 77%.",
  },
  {
    question: "Bayarnya bagaimana?",
    answer:
      "Lewat QRIS, buat akun gratis dulu, mulai dari review CV, lalu checkout Career OS Bundle langsung dari dashboard kamu.",
  },
];
